import React from 'react';
import { Linkedin, Code, Zap, Shield, Globe, ArrowRight, Star, Users, Download } from 'lucide-react';
import Logo from './Logo';

interface LandingPageProps {
  onLogin: (provider: 'google') => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onLogin }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-hpe-dark-bg via-hpe-dark-surface to-hpe-dark-bg text-white">
      {/* Header */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Logo className="w-10 h-10" />
            <span className="text-xl font-bold">OpenAPI to MCP</span>
          </div>
          
          {/* Creator Links */}
          <div className="flex items-center gap-4">
            <span className="text-hpe-gray-400 text-sm">Created by</span>
            <a 
              href="https://linkedin.com/in/vandewilly" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-hpe-gray-300 hover:text-hpe-primary transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              <span className="text-sm">Vandewilly</span>
            </a>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Beta Badge */}
          <div className="inline-flex items-center gap-2 bg-hpe-orange/20 text-hpe-orange px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Star className="w-4 h-4" />
            Beta Version - Early Access
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            Transform
            <span className="text-hpe-primary"> OpenAPI </span>
            to
            <span className="text-hpe-primary"> MCP Servers</span>
          </h1>
          
          <p className="text-xl text-hpe-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed">
            Convert your OpenAPI 3.x specifications into production-ready Model Context Protocol (MCP) servers 
            with comprehensive testing, documentation, and deployment configurations.
          </p>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="bg-hpe-dark-surface/50 rounded-lg p-6 border border-hpe-dark-border">
              <div className="flex items-center justify-center mb-3">
                <Download className="w-8 h-8 text-hpe-primary" />
              </div>
              <div className="text-2xl font-bold text-hpe-primary mb-1">15+</div>
              <div className="text-hpe-gray-400">Generated Files</div>
              <div className="text-sm text-hpe-gray-500 mt-1">Complete project structure</div>
            </div>
            
            <div className="bg-hpe-dark-surface/50 rounded-lg p-6 border border-hpe-dark-border">
              <div className="flex items-center justify-center mb-3">
                <Zap className="w-8 h-8 text-hpe-primary" />
              </div>
              <div className="text-2xl font-bold text-hpe-primary mb-1">FastMCP</div>
              <div className="text-hpe-gray-400">Framework</div>
              <div className="text-sm text-hpe-gray-500 mt-1">Optimized performance</div>
            </div>
            
            <div className="bg-hpe-dark-surface/50 rounded-lg p-6 border border-hpe-dark-border">
              <div className="flex items-center justify-center mb-3">
                <Shield className="w-8 h-8 text-hpe-primary" />
              </div>
              <div className="text-2xl font-bold text-hpe-primary mb-1">Enterprise</div>
              <div className="text-hpe-gray-400">Ready</div>
              <div className="text-sm text-hpe-gray-500 mt-1">Production configurations</div>
            </div>
          </div>

          {/* Login Buttons */}
          <div className="space-y-4">
            <p className="text-hpe-gray-300 mb-6">Sign in to start converting your APIs</p>
            
            <div className="flex justify-center">
              <button
                onClick={() => onLogin('google')}
                className="flex items-center justify-center gap-3 px-8 py-4 bg-white text-gray-800 rounded-lg font-medium hover:bg-gray-100 transition-colors shadow-lg"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold mb-4">What You Get</h2>
            <p className="text-hpe-gray-300 text-lg">A complete, production-ready MCP server package</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Feature Cards */}
            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <div className="w-12 h-12 bg-hpe-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Code className="w-6 h-6 text-hpe-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">FastMCP Framework</h3>
              <p className="text-hpe-gray-400">Modern, high-performance MCP server implementation with decorator-based tool definitions and automatic error handling.</p>
            </div>

            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <div className="w-12 h-12 bg-hpe-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-hpe-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Comprehensive Testing</h3>
              <p className="text-hpe-gray-400">Complete test suites with pytest, mocking, coverage reporting, and both unit and integration tests.</p>
            </div>

            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <div className="w-12 h-12 bg-hpe-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-hpe-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Multi-Platform Configs</h3>
              <p className="text-hpe-gray-400">Ready-to-use configurations for Claude Desktop, VSCode GitHub Copilot, and other MCP clients.</p>
            </div>

            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <div className="w-12 h-12 bg-hpe-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-hpe-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Developer Experience</h3>
              <p className="text-hpe-gray-400">Makefile commands, code formatting, type checking, and comprehensive documentation for seamless development.</p>
            </div>

            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <div className="w-12 h-12 bg-hpe-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-hpe-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">Smart Operation Filtering</h3>
              <p className="text-hpe-gray-400">Automatically categorize read/write operations with safety indicators and selective inclusion options.</p>
            </div>

            <div className="bg-hpe-dark-surface rounded-lg p-6 border border-hpe-dark-border">
              <div className="w-12 h-12 bg-hpe-primary/20 rounded-lg flex items-center justify-center mb-4">
                <Download className="w-6 h-6 text-hpe-primary" />
              </div>
              <h3 className="text-xl font-semibold mb-3">One-Click Download</h3>
              <p className="text-hpe-gray-400">Get your complete project as a ZIP file with all source code, tests, docs, and configurations ready to deploy.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-hpe-primary/20 to-hpe-blue/20 rounded-2xl p-8 border border-hpe-primary/30">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-hpe-gray-300 mb-6 text-lg">
              Join the beta and transform your APIs into powerful MCP servers in minutes.
            </p>
            <button
              onClick={() => onLogin('google')}
              className="inline-flex items-center gap-2 px-8 py-3 bg-hpe-primary hover:bg-hpe-primary-dark rounded-lg font-medium transition-colors"
            >
              Start Converting Now
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="container mx-auto px-4 py-8 border-t border-hpe-dark-border">
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2 text-hpe-gray-400">
            <span>© 2025 OpenAPI to MCP Converter</span>
            <span>•</span>
            <span>Beta Version</span>
          </div>
          
          <div className="flex items-center gap-6">
            <a 
              href="https://linkedin.com/in/vandewilly" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-hpe-gray-400 hover:text-hpe-primary transition-colors"
            >
              <Linkedin className="w-4 h-4" />
              Contact Creator
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;